/**
 * Main App Component
 * Sets up routing for the application
 */

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Pages
import { Dashboard } from './pages/Dashboard';
import { Analytics } from './pages/Analytics';
import AuthCallback from './pages/AuthCallback';
import Analyser from './pages/Analyser';
import AnalysisDetail from './pages/AnalysisDetail';
import AnalysisHistory from './pages/AnalysisHistory';
import Onboarding from './pages/Onboarding';
import InviteUser from './pages/admin/InviteUser';
import AdminUserList from './pages/admin/AdminUserList';
import AdminUserAnalytics from './pages/admin/AdminUserAnalytics';
import AdminUserHistory from './pages/admin/AdminUserHistory';
import AdminAnalysisDetail from './pages/admin/AdminAnalysisDetail';
import NotFound from './pages/NotFound';
import Marketing from './pages/Marketing';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Marketing />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              
            {/* Protected routes */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/invite"
              element={
                <ProtectedRoute requireRole="solas-admin">
                  <InviteUser />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireRole={['admin', 'solas-admin']}>
                  <AdminUserList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users/:id/analytics"
              element={
                <ProtectedRoute requireRole={['admin', 'solas-admin']}>
                  <AdminUserAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/users/:id/history"
              element={
                <ProtectedRoute requireRole={['admin', 'solas-admin']}>
                  <AdminUserHistory />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/analysis/:id"
              element={
                <ProtectedRoute requireRole={['admin', 'solas-admin']}>
                  <AdminAnalysisDetail />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/analyse"
              element={
                <ProtectedRoute>
                  <Analyser />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/analysis/:id"
              element={
                <ProtectedRoute>
                  <AnalysisDetail />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <AnalysisHistory />
                </ProtectedRoute>
              }
            />
            
            {/* Default route - removed, now marketing is at / */}
            {/* <Route path="/" element={<Navigate to="/dashboard" replace />} /> */}
              
              {/* 404 - Not Found */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
